import Dexie, { type Table } from 'dexie'
import {
  migrateVersion1To2,
  migrateVersion3To4,
  migrateVersion4To5,
  migrateVersion5To6,
  migrateVersion6To7,
  migrateVersion7To8,
  migrateVersion8To9,
  migrateVersion9To10,
} from '@/db/migrations'
import { DATABASE_NAME, DATABASE_SCHEMA_VERSION } from '@/db/version'
import type {
  AuditLog,
  Client,
  Counter,
  Inspection,
  InspectionResult,
  Machine,
  Photo,
  PhotoPreview,
  Settings,
  Signature,
  Technician,
} from '@/db/types'

const version1Stores = {
  clients: 'id, &code, normalizedName, normalizedFarmOrSite, normalizedPhone, activity, updatedAt',
  machines:
    'id, &code, clientId, [clientId+activity], normalizedEquipment, normalizedBrand, normalizedModel, normalizedSerialOrChassis, activity, updatedAt',
  machinePhotos: 'id, machineId, updatedAt',
  technicians: 'id, normalizedName, normalizedPhone, activity, updatedAt',
  inspections:
    'id, &reportNumber, clientId, machineId, technicianId, inspectionDate, status, updatedAt, [status+updatedAt], [clientId+inspectionDate], [machineId+inspectionDate], [technicianId+inspectionDate]',
  inspectionResults:
    'id, &[inspectionId+itemCode], inspectionId, systemCode, status, [inspectionId+systemCode], [inspectionId+status]',
  photos:
    'id, inspectionId, resultId, scope, createdAt, [inspectionId+createdAt], [resultId+createdAt]',
  signatures: 'id, inspectionId, technicianId, kind, signedAt, [inspectionId+kind]',
  settings: 'key, updatedAt',
  counters: 'key, updatedAt',
  inspectionEvents: 'id, inspectionId, type, at, [inspectionId+at]',
  backupHistory: 'id, kind, status, at',
} as const

const version2Stores = {
  clients:
    'id, &code, normalizedCompanyName, normalizedFarmOrSite, normalizedPhone, activeKey, createdAt, updatedAt, [activeKey+normalizedCompanyName]',
  machines:
    'id, &code, clientId, normalizedEquipment, normalizedBrand, normalizedModel, normalizedSerialChassis, activeKey, createdAt, updatedAt, [clientId+activeKey]',
  machinePhotos: 'id, machineId, updatedAt',
  technicians:
    'id, normalizedName, normalizedPhone, activeKey, createdAt, updatedAt, [activeKey+normalizedName]',
  inspections:
    'id, &reportNumber, clientId, machineId, technicianId, inspectionDate, status, priority, createdAt, updatedAt, [status+updatedAt], [clientId+inspectionDate], [machineId+inspectionDate], [technicianId+inspectionDate]',
  inspectionResults:
    'id, &[inspectionId+checklistItemId], inspectionId, checklistItemId, systemId, status, [inspectionId+systemId], [inspectionId+status]',
  photos:
    'id, inspectionId, machineId, resultId, category, createdAt, [inspectionId+order], [resultId+createdAt], [machineId+createdAt]',
  signatures:
    'id, inspectionId, technicianId, type, createdAt, [inspectionId+type], [technicianId+createdAt]',
  settings: 'key, updatedAt',
  counters: 'key, updatedAt',
  auditLogs:
    'id, entityType, entityId, action, occurredAt, [entityType+entityId], [entityType+occurredAt]',
  inspectionEvents: 'id, inspectionId, type, at, [inspectionId+at]',
  backupHistory: 'id, kind, status, at',
} as const

const version3Stores = {
  ...version2Stores,
  machinePhotos: null,
  inspectionEvents: null,
  backupHistory: null,
} as const

const version4Stores = { ...version3Stores } as const

const version5Stores = {
  ...version4Stores,
  photoPreviews:
    'id, &photoId, inspectionId, resultId, category, [inspectionId+order], [resultId+createdAt]',
} as const

const version6Stores = { ...version5Stores } as const

const version7Stores = {
  ...version6Stores,
  inspections:
    'id, &reportNumber, clientId, machineId, technicianId, normalizedClientName, normalizedClientPhone, normalizedFarmOrSite, normalizedMachineEquipment, normalizedMachineBrand, normalizedMachineModel, normalizedSerialChassis, normalizedTechnicianName, inspectionDate, status, priority, createdAt, updatedAt, [status+updatedAt], [clientId+inspectionDate], [machineId+inspectionDate], [technicianId+inspectionDate]',
} as const

const version8Stores = { ...version7Stores } as const

const version9Stores = {
  ...version8Stores,
  clients:
    'id, &code, normalizedCompanyName, normalizedPhone, activeKey, createdAt, updatedAt, [activeKey+normalizedCompanyName]',
  inspections:
    'id, &reportNumber, clientId, machineId, technicianId, normalizedClientName, normalizedClientPhone, normalizedMachineEquipment, normalizedMachineBrand, normalizedMachineModel, normalizedSerialChassis, normalizedTechnicianName, inspectionDate, status, priority, createdAt, updatedAt, [status+updatedAt], [clientId+inspectionDate], [machineId+inspectionDate], [technicianId+inspectionDate]',
} as const

const version10Stores = {
  ...version9Stores,
  photos:
    'id, inspectionId, machineId, resultId, category, createdAt, [inspectionId+order], [resultId+createdAt], [machineId+createdAt], [machineId+order]',
} as const

export { DATABASE_NAME, DATABASE_SCHEMA_VERSION }

export class RealMachinesDatabase extends Dexie {
  clients!: Table<Client, string>
  machines!: Table<Machine, string>
  technicians!: Table<Technician, string>
  inspections!: Table<Inspection, string>
  inspectionResults!: Table<InspectionResult, string>
  photos!: Table<Photo, string>
  photoPreviews!: Table<PhotoPreview, string>
  signatures!: Table<Signature, string>
  settings!: Table<Settings, string>
  counters!: Table<Counter, string>
  auditLogs!: Table<AuditLog, string>

  constructor(name = DATABASE_NAME) {
    super(name)
    this.version(1).stores(version1Stores)
    this.version(2).stores(version2Stores).upgrade(migrateVersion1To2)
    this.version(3).stores(version3Stores)
    this.version(4).stores(version4Stores).upgrade(migrateVersion3To4)
    this.version(5).stores(version5Stores).upgrade(migrateVersion4To5)
    this.version(6).stores(version6Stores).upgrade(migrateVersion5To6)
    this.version(7).stores(version7Stores).upgrade(migrateVersion6To7)
    this.version(8).stores(version8Stores).upgrade(migrateVersion7To8)
    this.version(9).stores(version9Stores).upgrade(migrateVersion8To9)
    this.version(DATABASE_SCHEMA_VERSION).stores(version10Stores).upgrade(migrateVersion9To10)
  }
}

let database: RealMachinesDatabase | undefined

export function getDatabase(): RealMachinesDatabase {
  database ??= new RealMachinesDatabase()
  return database
}
